import { useEffect } from 'react';

import parse from 'html-react-parser';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { useScrollToBottom } from 'use-scroll-to-bottom';

import Button from '../../../components/buttons/Button';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { useMarketingConsents } from '../../../hooks/useMarketingConsents';
import { auth } from '../../../services/auth';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';
import { convertCmsContent } from '../../../utils/convertCmsContent';
import { useSignUpWizardContext } from '../wizards/signUpWizard.provider';
import { useHandleSignUpWizard } from '../wizards/useHandleSIgnUpWizard';

import styles from './ResponsibleSocialPlay.module.scss';

const PAGE_SLUG = 'responsible-social-play';

interface ResponsibleSocialPlayProps extends WizardChildProps {
  onClose?: () => void;
  wizardProps?: WizardChildProps;
}
export const ResponsibleSocialPlay = observer((props: ResponsibleSocialPlayProps) => {
  const { isCurrent, prevStep, nextStep, onClose, wizardProps } = props;

  const { portalStore, cmsStore, liveGeoStore, marketingConsentStore, wizardStore } = useStores();

  const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;
  const { countryCode } = liveGeoStore.rawGeo ?? {};
  const { previousStep, step } = wizardStore;

  const { handleSignUpWizard } = useHandleSignUpWizard();
  const { credentials, provider } = useSignUpWizardContext();
  const { t } = useTranslation();

  const [setBottomRef, isBottom] = useScrollToBottom();

  const { areAllOptionalConsentsChecked } = useMarketingConsents({
    initialMarketingConsents: signUpMarketingConsents,
    fetchingMarketingConsents: fetchingSignUpMarketingConsent,
    localStorageKey: 'signup-marketing-consents',
  });

  const responsible_gaming_dialog = useFlag('responsible_gaming_dialog', ['']);
  const shouldShowRSGDialog = responsible_gaming_dialog.includes(countryCode || 'US') && !areAllOptionalConsentsChecked;

  useEffect(() => {
    if (!isCurrent || shouldShowRSGDialog) {
      return;
    }

    // if RSG dialog should not be shown, skip the step
    const isNextStep = step > (previousStep ?? 0);
    if (isNextStep) {
      nextStep();
    } else {
      prevStep();
    }
  }, [isCurrent, prevStep, areAllOptionalConsentsChecked, shouldShowRSGDialog, step, previousStep, nextStep]);

  const { value: cmsContent, loading: cmsContentLoading } = useAsync(async () => {
    const content = await cmsStore.fetchCmsPage(PAGE_SLUG);
    return parse(convertCmsContent(content) || '');
  }, [cmsStore]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      portalStore.closePortal();
    }
  };

  const handleGoBack = () => {
    if (onClose) {
      onClose();
    } else {
      prevStep();
    }
  };

  const handleSubmit = () => {
    const pendingAuthParams = auth.getAuthParams();
    handleSignUpWizard({
      ...pendingAuthParams,
      wizardProps,
      provider: provider,
      credentials: credentials,
    });
  };

  if (!shouldShowRSGDialog) {
    return null;
  }

  return (
    <div className={styles.responsibleDialogContainer} data-testid="responsible-social-play-dialog">
      <DialogHeader
        title={t('auth.responsibleSocialPlay')}
        titleClassName={styles.responsibleDialogHeader}
        onClose={handleClose}
        onGoBack={handleGoBack}
      />
      <DialogContent>
        <div className={styles.responsibleContainer}>
          <p className={styles.responsibleSubtitle}>{t('auth.pleaseReadTheTermsToProceed')}</p>
          <div className={styles.responsibleTermsSection}>
            <div className={styles.responsibleDescription} data-testid="responsible-social-play-dialog-content">
              {cmsContent}
            </div>
            <div ref={setBottomRef} />
          </div>
          <Button
            btnType="main"
            className={styles.responsibleAcceptButton}
            dataTestId="responsible-social-play-accept-btn"
            disabled={!isBottom || cmsContentLoading}
            onClick={handleSubmit}
          >
            {t('common.buttons.accept')}
          </Button>
        </div>
      </DialogContent>
    </div>
  );
});
